import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Suspense, type JSX } from 'react';
import { AppLoaderPage } from '@gokrew/ui/components/AppLoaderPage';
import { AppRouter } from '@gokrew/ui/components/AppRouter';
import NotFoundPage from '@gokrew/ui/pages/NotFound';
import { rememberTeamsPopup } from '@gokrew/ui/utility/teamsPopupReturn';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import { usePermissions } from '@/context/PermissionContext';
import { EmployeePortalMainLayout } from '@/layouts/EmployeePortalMainLayout';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useRouteWithPermission } from '@/hooks/useRouteWithPermission';
import { authRoutes, protectedRoutes } from './config';
import SlackLinkPage from '@/pages/slack/SlackLinkPage';
import EmployeeTeamsLinkPage from '@/pages/teams/TeamsLinkPage';
import { MockLoginPage } from '@/pages/auth/MockLoginPage';
import { MockSessionExpiredPage } from '@/pages/auth/MockSessionExpiredPage';
import { AuthSplitLayout } from '@/pages/auth/components/AuthSplitLayout';

// -----------------------------------------------------------------------------------------------------
// @ App routes
// -----------------------------------------------------------------------------------------------------

/**
 * Public, auth-independent routes that must render immediately without waiting
 * for silent session restore — otherwise the full-screen AppLoaderPage flashes
 * during `isInitializing` on a fresh load (e.g. after ending a mock session).
 */
const PUBLIC_STANDALONE_PATHS = ['/mock-session-expired', '/mock-login'];

export function AppRoutes() {
  const { isInitializing } = useEmployeePortalAuth();
  const isPublicStandalone = PUBLIC_STANDALONE_PATHS.includes(
    window.location.pathname
  );

  if (isInitializing && !isPublicStandalone) return <AppLoaderPage />;

  return (
    <AppRouter>
      <Suspense fallback={<AppLoaderPage />}>
        <Routes>
          {/* Root redirect — no layout */}
          <Route index element={<RootRedirect />} />

          {/* Auth layout routes */}
          <Route
            path="/"
            element={
              <AuthSplitLayout>
                <Outlet />
              </AuthSplitLayout>
            }
          >
            {/* Auth routes (login, forgot-password, reset-password) */}
            {authRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Route>

          {/* Mock login — sets token from query param and redirects to dashboard */}
          <Route path="/mock-login" element={<MockLoginPage />} />
          <Route
            path="/mock-session-expired"
            element={<MockSessionExpiredPage />}
          />

          {/* Slack link */}
          <Route
            path="/slack/link"
            element={
              <SlackAuthRoute>
                <SlackLinkPage />
              </SlackAuthRoute>
            }
          />

          {/* Teams link */}
          <Route
            path="/teams/link"
            element={
              <SlackAuthRoute
                tokenKey="teams_link_token"
                redirectTo="/teams/link"
              >
                <EmployeeTeamsLinkPage />
              </SlackAuthRoute>
            }
          />

          <Route
            path="/"
            element={
              <EmployeePrivateRoute>
                <EmployeePortalMainLayout />
              </EmployeePrivateRoute>
            }
          >
            {protectedRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <PermissionGuard
                    scope={route.scope}
                    fallback={<NoPermissionPage />}
                  >
                    {route.element}
                  </PermissionGuard>
                }
              >
                {route.children?.map((child, index) =>
                  child.index ? (
                    <Route key={index} index element={child.element} />
                  ) : (
                    <Route
                      key={child.path}
                      path={child.path}
                      element={
                        <PermissionGuard
                          scope={child.scope}
                          fallback={<NoPermissionPage />}
                        >
                          {child.element}
                        </PermissionGuard>
                      }
                    />
                  )
                )}
              </Route>
            ))}
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppRouter>
  );
}

// -----------------------------------------------------------------------------------------------------
// @ Private route guard
// -----------------------------------------------------------------------------------------------------

function EmployeePrivateRoute({ children }: { children: JSX.Element }) {
  const { token } = useEmployeePortalAuth();
  const { fetchingPermissions } = usePermissions();
  const location = useLocation();

  if (!token) {
    // The query matters too: email links open a tab with `?view=requests`.
    const redirect = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirect)}`}
        replace
      />
    );
  }
  if (fetchingPermissions) return <AppLoaderPage />;
  return children;
}

function SlackAuthRoute({
  children,
  tokenKey = 'slack_link_token',
  redirectTo = '/slack/link',
}: {
  children: JSX.Element;
  /** Must differ per provider so two link flows cannot clobber each other. */
  tokenKey?: string;
  redirectTo?: string;
}) {
  const { token } = useEmployeePortalAuth();
  const location = useLocation();

  if (!token) {
    const hash = location.hash;
    const match = hash.match(/[#&]token=([^&]*)/);
    if (match) {
      sessionStorage.setItem(tokenKey, decodeURIComponent(match[1]));
    }
    rememberTeamsPopup();
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
        replace
      />
    );
  }

  return children;
}

// Fallback rendered when a route's scope isn't held. Permissions are already
// resolved here (EmployeePrivateRoute gates on fetchingPermissions), so instead
// of showing a manual "access restricted" screen we auto-redirect to the first
// route the user actually has access to. The target always passes its own guard
// (or is the always-accessible fallback), so this can't loop.
function NoPermissionPage() {
  const redirectTo = useRouteWithPermission();
  return <Navigate to={redirectTo} replace />;
}

// Root ("/") redirect. Unauthenticated → login; authenticated → the default
// dashboard. If the user lacks dashboard access, the route's PermissionGuard
// fallback (NoPermissionPage) redirects them to a permitted route using
// fully-resolved permissions — so the decision never runs on a stale set.
function RootRedirect() {
  const { token } = useEmployeePortalAuth();

  if (!token) return <Navigate to="/login" replace />;
  return <Navigate to="/dashboard" replace />;
}
